/**
 * The session token, and where it lives between app opens (defect D2).
 *
 * A guest and a crew member are not Supabase Auth users. Their session is a JWT
 * minted by guest-login or staff-login and signed with the project secret, so
 * PostgREST accepts it and the policies read its claims. Nothing in supabase-js
 * knows about it, which means something here has to hold it and put it on every
 * request.
 *
 * Two rules the old code broke:
 *
 *   1. it has to be readable synchronously at boot, before React mounts, or the
 *      first reads of the first paint go out as anon and come back 401,
 *   2. it has to stop being used the moment it expires, or a phone that was
 *      signed in yesterday spends its morning sending a dead token and reading
 *      "permission denied" instead of "sign in again".
 */

export type TokenStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

/** localStorage where there is one, and a private object where there is not. */
export function defaultTokenStorage(): TokenStorage {
  try {
    const ls = globalThis.localStorage;
    if (ls) {
      // Touch it, because Safari in private mode has the object and throws.
      const probe = '__dia.probe__';
      ls.setItem(probe, '1');
      ls.removeItem(probe);
      return ls;
    }
  } catch {
    // Private mode, or storage the browser has taken away. Fall through.
  }
  return memoryStorage();
}

export function memoryStorage(): TokenStorage {
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => void map.set(key, value),
    removeItem: (key) => void map.delete(key),
  };
}

/** Milliseconds of clock skew we forgive before calling a token spent. */
export const EXPIRY_SKEW_MS = 30_000;

/**
 * The `exp` claim, in milliseconds, or null when the token does not carry one
 * (or is not a JWT at all, which is not our problem to diagnose here).
 */
export function tokenExpiresAt(token: string | null | undefined): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]!)) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** The `iat` claim, in milliseconds, or null when there is none. */
export function tokenIssuedAt(token: string | null | undefined): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]!)) as { iat?: unknown };
    return typeof payload.iat === 'number' ? payload.iat * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * True when the token is past its own expiry. A token with no expiry never
 * counts as expired: the server is the judge, and refusing it here would lock
 * somebody out over a token shape we did not anticipate.
 */
export function isTokenExpired(token: string | null | undefined, now = Date.now(), skewMs = EXPIRY_SKEW_MS): boolean {
  const at = tokenExpiresAt(token);
  if (at === null) return false;
  return at - skewMs <= now;
}

function base64UrlDecode(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = globalThis.atob(padded);
  // The claims are ASCII in practice, but a name with an accent would arrive as
  // UTF-8 bytes, so decode them properly rather than by character code.
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export type TokenHolderOptions = {
  key: string;
  storage?: TokenStorage;
  now?: () => number;
};

/**
 * One token, kept in memory and mirrored into storage so a reload finds it
 * before the first request goes out.
 */
export class TokenHolder {
  private token: string | null = null;
  private readonly key: string;
  private readonly storage: TokenStorage;
  private readonly now: () => number;
  private listeners = new Set<(token: string | null) => void>();

  constructor({ key, storage, now }: TokenHolderOptions) {
    this.key = key;
    this.storage = storage ?? defaultTokenStorage();
    this.now = now ?? (() => Date.now());
    this.token = this.read();
  }

  /** The live token, or null when there is none or it has expired. */
  get(): string | null {
    if (!this.token) return null;
    if (isTokenExpired(this.token, this.now())) {
      // Expiring is a state change, not an error. Drop it, tell whoever is
      // listening, and let the app put up its sign in screen.
      this.set(null);
      return null;
    }
    return this.token;
  }

  /** Whatever is stored, expired or not. For "should I sign this person out". */
  peek(): string | null {
    return this.token;
  }

  set(token: string | null): void {
    const next = token && token.length > 0 ? token : null;
    if (next === this.token) return;
    this.token = next;
    try {
      if (next) this.storage.setItem(this.key, next);
      else this.storage.removeItem(this.key);
    } catch {
      // Storage went away mid session. The token still works until the tab does.
    }
    for (const listener of this.listeners) listener(next);
  }

  subscribe(listener: (token: string | null) => void): () => void {
    this.listeners.add(listener);
    return () => void this.listeners.delete(listener);
  }

  private read(): string | null {
    try {
      const stored = this.storage.getItem(this.key);
      if (!stored) return null;
      // A token that died while the app was closed never gets used once.
      if (isTokenExpired(stored, this.now())) {
        this.storage.removeItem(this.key);
        return null;
      }
      return stored;
    } catch {
      return null;
    }
  }
}
